// src/store/slices/dropdownSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

const EMP_BASE = 'https://esystems.cdl.lk/backend-test/DGES_ERP/EMP';

const getHeaders = () => ({
  'Content-Type': 'application/json',
  'auth-key': localStorage.getItem('auth-key') || '',
});

const fetchJson = async (url) => {
  const res = await fetch(url, { method: 'GET', headers: getHeaders() });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
};

const safeResultSet = (r) => {
  if (r.status !== 'fulfilled') return [];
  const data = r.value;
  if (!data) return [];
  // Handle both { StatusCode, ResultSet } and direct array
  if (Array.isArray(data)) return data;
  if (data.StatusCode === 200 && Array.isArray(data.ResultSet)) return data.ResultSet;
  if (data.StatusCode === 200 && data.ResultSet && typeof data.ResultSet === 'object') return [data.ResultSet];
  return [];
};

export const fetchAllDropdowns = createAsyncThunk(
  'dropdowns/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const results = await Promise.allSettled([
        fetchJson(`${EMP_BASE}/GetEMPCat`),
        fetchJson(`${EMP_BASE}/GetProfDetails`),
        fetchJson(`${EMP_BASE}/GetLocDetails`),
        fetchJson(`${EMP_BASE}/GetDepDetails`),
        fetchJson(`${EMP_BASE}/GetDevDetails`),
        fetchJson(`${EMP_BASE}/GetShiftTypes`),
        fetchJson(`${EMP_BASE}/GetShiftRules`),
        fetchJson(`${EMP_BASE}/GetProDetails`),
        fetchJson(`${EMP_BASE}/GetDisDetails`),
        fetchJson(`${EMP_BASE}/GetPoliceStDetails`),
        fetchJson(`${EMP_BASE}/GetELEDetails`),
        fetchJson(`${EMP_BASE}/GetCDLCal`),
      ]);

      const [empCat, profDetails, locDetails, depDetails, devDetails,
             shiftTypes, shiftRules, proDetails, disDetails, policeStDetails, eleDetails, cdlCal] = results;

      return {
        empCat:           safeResultSet(empCat),
        profDetails:      safeResultSet(profDetails),
        locDetails:       safeResultSet(locDetails),
        depDetails:       safeResultSet(depDetails),
        devDetails:       safeResultSet(devDetails),
        shiftTypes:       safeResultSet(shiftTypes),
        shiftRules:       safeResultSet(shiftRules),
        proDetails:       safeResultSet(proDetails),
        disDetails:       safeResultSet(disDetails),
        policeStDetails:  safeResultSet(policeStDetails),
        eleDetails:       safeResultSet(eleDetails),
        cdlCal:           safeResultSet(cdlCal),
      };
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to load dropdown data');
    }
  }
);

const dropdownSlice = createSlice({
  name: 'dropdowns',
  initialState: {
    empCat: [], profDetails: [], locDetails: [], depDetails: [],
    devDetails: [], shiftTypes: [], shiftRules: [], proDetails: [],
    disDetails: [], policeStDetails: [], eleDetails: [], cdlCal: [],
    loading: false, loaded: false, error: null,
  },
  reducers: {
    resetDropdowns(state) { state.loaded = false; state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllDropdowns.pending,    (state) => { state.loading = true; state.error = null; })
      .addCase(fetchAllDropdowns.fulfilled,  (state, { payload }) => {
        state.loading = false; state.loaded = true;
        Object.assign(state, payload);
      })
      .addCase(fetchAllDropdowns.rejected,   (state, { payload }) => {
        state.loading = false; state.error = payload;
      });
  },
});

export const { resetDropdowns } = dropdownSlice.actions;
export default dropdownSlice.reducer;
